import React, { useState, useEffect, useRef, useMemo, useCallback, useId } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate, useLocation } from "react-router-dom";
import { getColumns } from "./columns";
import styles from "./EnhancedStockTable.module.css";
import { describeChecked, freshestCheck } from "../../utils/lastChecked";
import {
  Snackbar,
  Alert,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  Button,
  Pagination,
  CircularProgress,
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select
} from "@mui/material";
import { keyframes } from "@mui/system";
import { fetchPaginatedStocks } from "../../utils/StockApiUtils";
import { CARDS_PER_PAGE, LISTING_SORT_OPTIONS, findListingSortOption } from "../../utils/listingDefaults";
import { TrackingService } from "../../TrackingService";
import { useAuth } from "../../context/AuthContext";
import BulkActionToolbar from "../BulkActionToolbar";
import { formatCurrency, formatPercent, getDisplayPrice } from "../../utils/priceUtils";

const isPriceSortField = (field) => field === 'currentPrice' || field === 'regularPrice';

// A real discount at any retailer counts as a deal, even when the listing
// shows another retailer's price. Mirrors the backend's onSale filter.
const getRetailerSale = (row) => (row?.retailerOffers || []).find(
  (offer) => offer?.onSale
    && typeof offer.salePrice === 'number'
    && typeof offer.regularPrice === 'number'
    && offer.salePrice > 0
    && offer.salePrice < offer.regularPrice
) || null;

const statusPing = keyframes`
  0% { transform: scale(1); opacity: 0.45; }
  70% { transform: scale(2.1); opacity: 0; }
  100% { transform: scale(2.1); opacity: 0; }
`;

// Which card to scroll back to when the visitor returns from a product page (#390).
// `entry` is the listing's own history entry, so only going Back to it counts as a return;
// opening the same listing again later is a new entry and starts at the top.
const RETURN_TARGET_KEY = 'stockmaid:listing-return';

const saveReturnTarget = (url, entry, sku) => {
  try {
    sessionStorage.setItem(RETURN_TARGET_KEY, JSON.stringify({ url, entry, sku }));
  } catch (storageError) {
    // Private mode or a full store: the listing simply opens at the top.
  }
};

const takeReturnTarget = () => {
  try {
    const raw = sessionStorage.getItem(RETURN_TARGET_KEY);
    sessionStorage.removeItem(RETURN_TARGET_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (storageError) {
    return null;
  }
};

// "Checked Ns ago" ticks while the page is open (#283). The clock lives in this line, so a
// tick re-renders one line of text and not every card on the page.
const CardChecked = ({ row }) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // The freshest check across every attached retailer; each retailer's own
  // time is on the product page (#283).
  const freshest = freshestCheck(row);
  if (!freshest) {
    return null;
  }
  const checked = describeChecked(freshest.value, freshest.poll, { now, short: true });
  const detail = freshest.all
    .map((c) => `${c.label}: ${describeChecked(c.value, c.poll, { now }).text}`)
    .join(' · ');
  return (
    <div
      className={`${styles.card_checked} ${checked.stale ? styles.card_checked_stale : ''}`}
      title={`Last checked - ${detail}`}
    >
      <span className={styles.card_checked_dot} aria-hidden="true" />
      Checked {checked.text}
    </div>
  );
};

export const EnhancedStockTable = ({ 
  stock = null,
  filters = {}, 
  searchTerm = "",
  displayMode = "table",
  initialSortModel = [{ field: "name", sort: "asc" }],
  paginationModel: controlledPaginationModel = null,
  onPaginationModelChange = null,
  sortModel: controlledSortModel = null,
  onSortModelChange = null,
  tableTitle = "Products",
  itemLabel = "products",
  enableCardSelection = false,
  cardSelection = [],
  onCardSelectionChange = null,
  onTrackingChange = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { isAuthenticated, isPremium, login } = useAuth();
  const isGrid = displayMode === "cards";
  const defaultPageSize = isGrid ? CARDS_PER_PAGE : 10;
  const sortLabelId = useId();

  const columns = useMemo(() => {
    const allColumns = getColumns(isMobile);
    
    if (isMobile) {
      return allColumns.filter(col => 
        ['thumbnailImage', 'name', 'regularPrice'].includes(col.field)
      );
    } else if (isTablet) {
      return allColumns.filter(col => col.field !== 'manufacturer');
    }
    
    return allColumns;
  }, [isMobile, isTablet]);

  const isClientSide = stock !== null;

  const [internalPaginationModel, setInternalPaginationModel] = useState({
    page: 0,
    pageSize: defaultPageSize
  });
  const [internalSortModel, setInternalSortModel] = useState(
    Array.isArray(initialSortModel) && initialSortModel.length > 0
      ? initialSortModel
      : [{ field: 'name', sort: 'asc' }]
  );
  const [stocks, setStocks] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isBulkTracking, setIsBulkTracking] = useState(false);
  const [trackedSkus, setTrackedSkus] = useState(new Set());
  const [trackingLoading, setTrackingLoading] = useState(new Set());
  const resultsRef = useRef(null);
  const paginationModel = controlledPaginationModel ?? internalPaginationModel;
  const sortModel = controlledSortModel ?? internalSortModel;
  const activeSortField = sortModel[0]?.field || 'name';
  const activeSortDirection = sortModel[0]?.sort || 'asc';

  // `replace` marks a correction rather than a visitor's page turn, for a parent that
  // keeps the page in the URL: a correction must not add a history entry.
  const updatePaginationModel = useCallback((nextModel, { replace = false } = {}) => {
    const resolvedModel = typeof nextModel === 'function' ? nextModel(paginationModel) : nextModel;

    if (!controlledPaginationModel) {
      setInternalPaginationModel(resolvedModel);
    }

    if (onPaginationModelChange) {
      onPaginationModelChange(resolvedModel, { replace });
    }
  }, [controlledPaginationModel, onPaginationModelChange, paginationModel]);
  const updatePaginationModelRef = useRef(updatePaginationModel);
  updatePaginationModelRef.current = updatePaginationModel;

  // A new page starts at the top, for Back and Forward as well as the pager. Instant: a
  // smooth scroll over a dozen rows of cards takes longer than reading the first row.
  const shownPageRef = useRef(paginationModel.page);
  useEffect(() => {
    if (shownPageRef.current === paginationModel.page) {
      return;
    }
    shownPageRef.current = paginationModel.page;
    window.scrollTo(0, 0);
  }, [paginationModel.page]);

  const updateSortModel = useCallback((nextModel) => {
    const resolvedModel = Array.isArray(nextModel) ? nextModel : [];

    if (!controlledSortModel) {
      setInternalSortModel(resolvedModel);
    }

    if (onSortModelChange) {
      onSortModelChange(resolvedModel);
    }
  }, [controlledSortModel, onSortModelChange]);

  useEffect(() => {
    if (controlledPaginationModel) {
      return;
    }

    setInternalPaginationModel((prev) => ({
      ...prev,
      page: 0,
      pageSize: defaultPageSize
    }));
  }, [controlledPaginationModel, defaultPageSize]);

  const stableFilters = useMemo(() => JSON.stringify(filters), [filters]);
  const stableSearchTerm = useMemo(() => searchTerm, [searchTerm]);
  const parsedFilters = useMemo(() => {
    try {
      return JSON.parse(stableFilters) || {};
    } catch (parseError) {
      console.error('Error parsing filters:', parseError);
      return {};
    }
  }, [stableFilters]);

  // A new filter or search starts again at page 1. Only when one of them changes: this used
  // to run on every page turn too and sent a listing that pages itself straight back to page 1.
  const filterKey = `${stableFilters}|${stableSearchTerm}`;
  const appliedFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (appliedFilterKeyRef.current === filterKey) {
      return;
    }
    appliedFilterKeyRef.current = filterKey;

    if (controlledPaginationModel || paginationModel.page === 0) {
      return;
    }

    updatePaginationModel({
      ...paginationModel,
      page: 0
    });
  }, [controlledPaginationModel, filterKey, paginationModel, updatePaginationModel]);

  useEffect(() => {
    if (isClientSide) {
      setStocks(stock || []);
      setLoading(false);
      return;
    }

    // The latest request wins. A page, filter or sort change made while an earlier request
    // is still in flight starts its own request, and the earlier answer is thrown away.
    let isLatest = true;

    const fetchData = async () => {
      let pageCorrected = false;
      setLoading(true);

      try {
        const pageData = await fetchPaginatedStocks({
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortField: activeSortField,
          sortDirection: activeSortDirection,
          filters: {
            ...JSON.parse(stableFilters),
            searchTerm: stableSearchTerm || undefined
          }
        });
        if (!isLatest) {
          return;
        }

        const totalElements = pageData.totalElements || 0;
        const lastPage = Math.max(0, Math.ceil(totalElements / paginationModel.pageSize) - 1);
        if (paginationModel.page > lastPage) {
          // A page past the end (an old link, or a list that got shorter): show the last
          // real page instead of an empty grid.
          pageCorrected = true;
          updatePaginationModelRef.current(
            { page: lastPage, pageSize: paginationModel.pageSize },
            { replace: true }
          );
          return;
        }

        setStocks(pageData.content || []);
        setRowCount(totalElements);
        setError(null);
      } catch (error) {
        if (!isLatest) {
          return;
        }
        console.error('Error fetching stocks:', error);
        setError('Failed to load products. Please try again.');
        setStocks([]);
        setRowCount(0);
      } finally {
        // After a correction the next request is already on its way; keep the spinner.
        if (isLatest && !pageCorrected) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isLatest = false;
    };
  }, [
    isClientSide,
    stock,
    paginationModel.page,
    paginationModel.pageSize,
    activeSortField,
    activeSortDirection,
    stableFilters,
    stableSearchTerm
  ]);

  useEffect(() => {
    if (!isAuthenticated) {
      setTrackedSkus(new Set());
      return;
    }

    let isMounted = true;

    const fetchTrackedSkus = async () => {
      try {
        const tracked = await TrackingService.getUserTrackedStocks();
        if (!isMounted) {
          return;
        }
        const nextSet = new Set(tracked.map((item) => item.stockSku));
        setTrackedSkus(nextSet);
      } catch (fetchError) {
        console.error('Error loading tracked products:', fetchError);
      }
    };

    fetchTrackedSkus();

    return () => {
      isMounted = false;
    };
  }, [isAuthenticated]);

  const getRowClassName = (params) => {
    const isAvailable = params.row.inStoreAvailability || params.row.onlineAvailability;
    return isAvailable ? styles.row_in_stock : styles.row_out_of_stock;
  };

  const handleRowClick = (params) => {
    const listingUrl = `${location.pathname}${location.search}`;
    saveReturnTarget(listingUrl, location.key, params.row.sku);
    navigate(`/skus/${params.row.sku}`, {
      state: { from: listingUrl }
    });
  };

  const handleCloseError = () => {
    setError(null);
  };

  const showSnackbar = (message, severity = 'success') => {
    setError(severity === 'error' ? message : null);
    if (severity === 'success' || severity === 'warning') {
      setError(null);
      setTimeout(() => {
        const event = new CustomEvent('show-snackbar', { 
          detail: { message, severity } 
        });
        window.dispatchEvent(event);
      }, 0);
    }
  };

  const handleTrackClick = async (event, stockRow) => {
    event.stopPropagation();

    if (!isAuthenticated) {
      localStorage.setItem('redirectAfterLogin', `${window.location.pathname}${window.location.search}`);
      login();
      return;
    }

    if (!isPremium) {
      navigate('/subscription');
      return;
    }

    try {
      setTrackingLoading((prev) => new Set(prev).add(stockRow.sku));
      if (trackedSkus.has(stockRow.sku)) {
        await TrackingService.removeTracking(stockRow.sku);
        setTrackedSkus((prev) => {
          const next = new Set(prev);
          next.delete(stockRow.sku);
          return next;
        });
        showSnackbar(`Stopped tracking ${stockRow.name || 'this product'}.`, 'success');
        if (onTrackingChange) {
          onTrackingChange(stockRow.sku, false);
        }
      } else {
        await TrackingService.createTracking(stockRow.sku);
        setTrackedSkus((prev) => new Set(prev).add(stockRow.sku));
        showSnackbar(
          `Tracking ${stockRow.name || 'this product'}. We'll alert you when it's available.`,
          'success'
        );
        if (onTrackingChange) {
          onTrackingChange(stockRow.sku, true);
        }
      }
    } catch (error) {
      showSnackbar(error.message || 'Error tracking product', 'error');
    } finally {
      setTrackingLoading((prev) => {
        const next = new Set(prev);
        next.delete(stockRow.sku);
        return next;
      });
    }
  };

  const renderAvailabilityPill = (label, isAvailable) => (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.35,
        borderRadius: 999,
        bgcolor: isAvailable ? 'rgba(34,197,94,0.14)' : 'rgba(239,68,68,0.14)',
        color: isAvailable ? 'var(--sm-success-strong)' : 'var(--sm-danger-strong)'
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: isAvailable ? 'var(--sm-success-bright)' : 'var(--sm-danger)',
          '&::after': {
            content: '""',
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            bgcolor: isAvailable ? 'var(--sm-success-bright)' : 'var(--sm-danger)',
            animation: `${statusPing} 1.8s ease-out infinite`
          }
        }}
      />
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );

  const handleBulkTrack = async () => {
    if (!isAuthenticated) {
      login();
      return;
    }

    if (!isPremium) {
      navigate('/subscription');
      return;
    }

    setIsBulkTracking(true);

    try {
      const results = await TrackingService.createBulkTracking(selectedRows);
      const successCount = results.length;
      const requestedCount = selectedRows.length;

      if (successCount < requestedCount) {
        const failedCount = requestedCount - successCount;
        showSnackbar(
          `Tracked ${successCount} product(s). ${failedCount} already tracked or unavailable.`,
          'warning'
        );
      } else {
        showSnackbar(
          `Successfully tracking ${successCount} product${successCount !== 1 ? 's' : ''}!`,
          'success'
        );
      }

      setSelectedRows([]);

    } catch (error) {
      console.error('Error with bulk tracking:', error);
      showSnackbar(error.message || 'Error tracking products', 'error');
    } finally {
      setIsBulkTracking(false);
    }
  };

  const handleClearSelection = () => {
    setSelectedRows([]);
  };

  const sortOptions = useMemo(() => {
    // The two restock orders need the API; a listing sorted in the browser cannot offer them.
    const options = LISTING_SORT_OPTIONS.filter((option) => !(isClientSide && option.serverOnly));
    const activeOption = findListingSortOption(activeSortField, activeSortDirection);
    if (activeOption && options.includes(activeOption)) {
      return { options, activeKey: activeOption.key };
    }
    // A page that opens in an order of its own keeps it on the menu, so it can be picked again.
    return {
      options: [
        { key: 'pageDefault', label: 'Default', model: [{ field: activeSortField, sort: activeSortDirection }] },
        ...options
      ],
      activeKey: 'pageDefault'
    };
  }, [isClientSide, activeSortField, activeSortDirection]);

  const handleSortChange = (event) => {
    const nextOption = sortOptions.options.find((option) => option.key === event.target.value);
    if (!nextOption || nextOption.key === sortOptions.activeKey) {
      return;
    }
    updateSortModel(nextOption.model);
    // A parent that owns the order goes back to page 1 itself, in the same URL change; a
    // second update from here would be built from the old URL and undo the new order.
    if (!controlledSortModel && paginationModel.page !== 0) {
      updatePaginationModel({ ...paginationModel, page: 0 });
    }
  };

  const filteredStocks = useMemo(() => {
    if (!isClientSide) {
      return stocks;
    }

    let nextStocks = [...stocks];
    const normalizedSearch = (stableSearchTerm || '').trim().toLowerCase();

    if (normalizedSearch.length >= 2) {
      nextStocks = nextStocks.filter((row) => {
        const name = String(row?.name || '').toLowerCase();
        const manufacturer = String(row?.manufacturer || '').toLowerCase();
        const sku = String(row?.sku || '').toLowerCase();
        return (
          name.includes(normalizedSearch) ||
          manufacturer.includes(normalizedSearch) ||
          sku.includes(normalizedSearch)
        );
      });
    }

    if (Array.isArray(parsedFilters.manufacturers) && parsedFilters.manufacturers.length > 0) {
      const manufacturerSet = new Set(
        parsedFilters.manufacturers
          .filter((entry) => typeof entry === 'string' && entry.trim().length > 0)
          .map((entry) => entry.trim().toLowerCase())
      );
      nextStocks = nextStocks.filter((row) =>
        manufacturerSet.has(String(row?.manufacturer || '').trim().toLowerCase())
      );
    }

    if (Array.isArray(parsedFilters.categories) && parsedFilters.categories.length > 0) {
      const categorySet = new Set(
        parsedFilters.categories
          .filter((entry) => typeof entry === 'string' && entry.trim().length > 0)
          .map((entry) => entry.trim().toUpperCase())
      );
      nextStocks = nextStocks.filter((row) =>
        categorySet.has(String(row?.productCategory || '').trim().toUpperCase())
      );
    }

    if (Array.isArray(parsedFilters.series) && parsedFilters.series.length > 0) {
      const seriesTokens = parsedFilters.series
        .filter((entry) => typeof entry === 'string' && entry.trim().length > 0)
        .map((entry) => entry.trim().toLowerCase());
      nextStocks = nextStocks.filter((row) => {
        const name = String(row?.name || '').toLowerCase();
        return seriesTokens.some((seriesToken) => name.includes(seriesToken));
      });
    }

    if (parsedFilters.minPrice !== undefined && parsedFilters.minPrice !== null) {
      const minPrice = Number(parsedFilters.minPrice);
      if (!Number.isNaN(minPrice)) {
        nextStocks = nextStocks.filter((row) => {
          const price = getDisplayPrice(row);
          return typeof price === 'number' && price >= minPrice;
        });
      }
    }

    if (parsedFilters.maxPrice !== undefined && parsedFilters.maxPrice !== null) {
      const maxPrice = Number(parsedFilters.maxPrice);
      if (!Number.isNaN(maxPrice)) {
        nextStocks = nextStocks.filter((row) => {
          const price = getDisplayPrice(row);
          return typeof price === 'number' && price <= maxPrice;
        });
      }
    }

    if (parsedFilters.onSale) {
      nextStocks = nextStocks.filter((row) => !!row?.onSale || !!getRetailerSale(row));
    }

    if (parsedFilters.anyAvailable) {
      nextStocks = nextStocks.filter((row) => !!row?.inStoreAvailability || !!row?.onlineAvailability);
    } else {
      if (parsedFilters.inStoreAvailable) {
        nextStocks = nextStocks.filter((row) => !!row?.inStoreAvailability);
      }
      if (parsedFilters.onlineAvailable) {
        nextStocks = nextStocks.filter((row) => !!row?.onlineAvailability);
      }
    }

    if (parsedFilters.releaseDateFrom) {
      const fromDate = new Date(parsedFilters.releaseDateFrom);
      if (!Number.isNaN(fromDate.getTime())) {
        nextStocks = nextStocks.filter((row) => {
          if (!row?.releaseDate) {
            return false;
          }
          const releaseDate = new Date(row.releaseDate);
          return !Number.isNaN(releaseDate.getTime()) && releaseDate >= fromDate;
        });
      }
    }

    if (parsedFilters.releaseDateTo) {
      const toDate = new Date(parsedFilters.releaseDateTo);
      if (!Number.isNaN(toDate.getTime())) {
        nextStocks = nextStocks.filter((row) => {
          if (!row?.releaseDate) {
            return false;
          }
          const releaseDate = new Date(row.releaseDate);
          return !Number.isNaN(releaseDate.getTime()) && releaseDate <= toDate;
        });
      }
    }

    return nextStocks;
  }, [isClientSide, stocks, stableSearchTerm, parsedFilters]);

  const effectiveRowCount = isClientSide ? filteredStocks.length : rowCount;
  const totalPages = Math.max(1, Math.ceil(effectiveRowCount / paginationModel.pageSize));
  const firstShown = paginationModel.page * paginationModel.pageSize + 1;
  const lastShown = Math.min(effectiveRowCount, (paginationModel.page + 1) * paginationModel.pageSize);
  const countLabel = totalPages > 1 && firstShown <= lastShown
    ? `Showing ${firstShown}–${lastShown} of ${effectiveRowCount} ${itemLabel}`
    : `Showing ${effectiveRowCount} ${itemLabel}`;

  useEffect(() => {
    if (!isClientSide) {
      return;
    }

    const maxPage = Math.max(0, Math.ceil(filteredStocks.length / paginationModel.pageSize) - 1);
    if (paginationModel.page > maxPage) {
      updatePaginationModel((prev) => ({ ...prev, page: maxPage }));
    }
  }, [isClientSide, filteredStocks.length, paginationModel.page, paginationModel.pageSize, updatePaginationModel]);

  const sortedStocks = useMemo(() => {
    if (!isClientSide) {
      return stocks;
    }

    const sorted = [...filteredStocks];
    const sortField = sortModel[0]?.field || 'name';
    const sortDirection = sortModel[0]?.sort || 'asc';

    sorted.sort((a, b) => {
      const aValue = a?.[sortField];
      const bValue = b?.[sortField];

      if (isPriceSortField(sortField)) {
        const aDisplayPrice = getDisplayPrice(a);
        const bDisplayPrice = getDisplayPrice(b);
        const safeA = typeof aDisplayPrice === 'number' ? aDisplayPrice : Number.POSITIVE_INFINITY;
        const safeB = typeof bDisplayPrice === 'number' ? bDisplayPrice : Number.POSITIVE_INFINITY;
        return sortDirection === 'asc' ? safeA - safeB : safeB - safeA;
      }

      if (aValue === undefined || aValue === null) return 1;
      if (bValue === undefined || bValue === null) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      const aString = String(aValue).toLowerCase();
      const bString = String(bValue).toLowerCase();
      if (aString === bString) return 0;
      if (sortDirection === 'asc') {
        return aString < bString ? -1 : 1;
      }
      return aString > bString ? -1 : 1;
    });

    return sorted;
  }, [isClientSide, stocks, filteredStocks, sortModel]);

  const visibleStocks = useMemo(() => {
    if (!isClientSide) {
      return sortedStocks;
    }

    const start = paginationModel.page * paginationModel.pageSize;
    const end = start + paginationModel.pageSize;
    return sortedStocks.slice(start, end);
  }, [isClientSide, paginationModel.page, paginationModel.pageSize, sortedStocks]);

  // Back from a product page: return to the card that was opened, once the cards exist.
  // With 48 cards a page, landing at the top again would lose the visitor's place.
  const returnHandledRef = useRef(false);
  useEffect(() => {
    if (returnHandledRef.current || loading || visibleStocks.length === 0) {
      return;
    }
    returnHandledRef.current = true;

    // Taken (and so removed) on every listing load, whether or not it is used.
    const target = takeReturnTarget();
    const sku = Number(target?.sku);
    // Back lands on the entry the card was opened from; the product page's own back button
    // opens a new entry and says so in its navigation state.
    const isReturn = target?.entry === location.key || location.state?.returnToCard === true;
    if (!target || !isReturn || target.url !== `${location.pathname}${location.search}` || !Number.isFinite(sku)) {
      return;
    }
    const card = resultsRef.current?.querySelector(`[data-sku="${sku}"]`);
    if (card && typeof card.scrollIntoView === 'function') {
      card.scrollIntoView({ block: 'center' });
    }
  }, [loading, visibleStocks, location.pathname, location.search, location.key, location.state]);

  const renderGrid = () => {
    if (loading && stocks.length === 0) {
      return (
        <Box className={styles.grid_loading}>
          <CircularProgress size={32} />
          <Typography variant="body2" color="text.secondary">
            Loading products...
          </Typography>
        </Box>
      );
    }

    return (
      <Box className={styles.grid_container} ref={resultsRef}>
        <div className={styles.grid_toolbar}>
          <div className={styles.grid_meta}>
            <Typography variant="h6" component="h2">
              {tableTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {countLabel}
            </Typography>
          </div>
          <div className={styles.grid_controls}>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id={sortLabelId}>Sort by</InputLabel>
              <Select
                labelId={sortLabelId}
                label="Sort by"
                value={sortOptions.activeKey}
                onChange={handleSortChange}
                sx={{ fontWeight: 600, fontSize: '0.9rem' }}
              >
                {sortOptions.options.map((option) => (
                  <MenuItem key={option.key} value={option.key}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        <div
          className={`${styles.card_grid} ${loading ? styles.card_grid_loading : ''}`}
          aria-busy={loading}
        >
          {visibleStocks.map((row) => {
            const isTracked = trackedSkus.has(row.sku);
            const isTrackingBusy = trackingLoading.has(row.sku);
            const isSelected = cardSelection.includes(row.sku);
            const imageSrc = row.image || row.thumbnailImage || row.alternateViewsImage;
            return (
              <div
                key={row.sku}
                className={styles.card}
                data-sku={row.sku}
                onClick={() => handleRowClick({ row })}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleRowClick({ row });
                  }
                }}
              >
                {enableCardSelection && (
                  <div className={styles.card_select}>
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onClick={(event) => event.stopPropagation()}
                      onChange={() => {
                        if (!onCardSelectionChange) {
                          return;
                        }
                        if (isSelected) {
                          onCardSelectionChange(cardSelection.filter((sku) => sku !== row.sku));
                        } else {
                          onCardSelectionChange([...cardSelection, row.sku]);
                        }
                      }}
                    />
                  </div>
                )}
                <div className={styles.card_media}>
                  {imageSrc && <img src={imageSrc} alt={row.name} loading="lazy" />}
                </div>
                <div className={styles.card_body}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" className={styles.card_name} title={row.name}>
                      {row.name}
                    </Typography>
                    {row.hasMcOffer && (
                      <Box
                        sx={{
                          px: 0.8,
                          py: 0.2,
                          borderRadius: 999,
                          bgcolor: '#1976d2',
                          color: '#fff',
                          fontSize: '0.66rem',
                          fontWeight: 700
                        }}
                      >
                        MC
                      </Box>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {row.manufacturer}
                  </Typography>
                  {row.onSale && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.45 }}>
                      <Box
                        sx={{
                          alignSelf: 'flex-start',
                          px: 1,
                          py: 0.25,
                          borderRadius: 999,
                          bgcolor: 'rgba(22,163,74,0.14)',
                          color: 'var(--sm-success-strong)',
                          fontSize: '0.68rem',
                          fontWeight: 800,
                          letterSpacing: 0.25,
                          textTransform: 'uppercase'
                        }}
                      >
                        Deal
                      </Box>
                      {(row.dollarSavings || row.percentSavings) && (
                        <Typography sx={{ fontSize: '0.76rem', color: 'var(--sm-success-strong)', fontWeight: 700, lineHeight: 1.22 }}>
                          Save {formatCurrency(row.dollarSavings) || ''}
                          {row.percentSavings ? ` (${formatPercent(row.percentSavings)} off)` : ''}
                        </Typography>
                      )}
                    </Box>
                  )}
                  <div className={styles.card_row}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                      <Typography
                        variant="subtitle1"
                        className={styles.card_price}
                        sx={{ color: row.onSale ? 'var(--sm-success-strong)' : 'inherit' }}
                      >
                        {formatCurrency(getDisplayPrice(row)) || '—'}
                      </Typography>
                      {row.onSale && (
                        <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', textDecoration: 'line-through' }}>
                          {formatCurrency(row.regularPrice) || '—'}
                        </Typography>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.7, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {renderAvailabilityPill('In-store', !!row.inStoreAvailability)}
                      {renderAvailabilityPill('Online', !!row.onlineAvailability)}
                    </Box>
                  </div>
                  <CardChecked row={row} />
                </div>
                <div className={styles.card_actions}>
                  <Button
                    size="small"
                    variant={isTracked ? "outlined" : "contained"}
                    sx={
                      isTracked
                        ? {
                            borderColor: 'var(--sm-btn-contained-bg)',
                            color: 'var(--sm-btn-contained-bg)',
                            fontWeight: 700,
                            '&:hover': {
                              borderColor: 'var(--sm-btn-contained-hover)',
                              backgroundColor: 'rgba(17,24,39,0.04)'
                            }
                          }
                        : {
                            bgcolor: 'var(--sm-btn-contained-bg)',
                            color: 'var(--sm-btn-contained-text)',
                            fontWeight: 700,
                            '&:hover': {
                              bgcolor: 'var(--sm-btn-contained-hover)'
                            }
                          }
                    }
                    onClick={(event) => handleTrackClick(event, row)}
                    disabled={isTrackingBusy}
                  >
                    {isTracked ? "Untrack" : "Track"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: 'var(--sm-border-strong)',
                      color: 'var(--sm-btn-contained-bg)',
                      fontWeight: 700,
                      '&:hover': {
                        borderColor: 'var(--sm-muted-color)',
                        backgroundColor: 'rgba(148,163,184,0.12)'
                      }
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleRowClick({ row });
                    }}
                  >
                    Details
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {totalPages > 1 && (
          <div className={styles.grid_footer}>
            <Pagination
              count={totalPages}
              page={paginationModel.page + 1}
              onChange={(event, value) => {
                // The pager also reports a click on the page already showing.
                if (value - 1 !== paginationModel.page) {
                  updatePaginationModel((prev) => ({ ...prev, page: value - 1 }));
                }
              }}
              color="primary"
            />
          </div>
        )}
      </Box>
    );
  };

  if (effectiveRowCount === 0 && !loading && !error) {
    if (stableSearchTerm && stableSearchTerm.length >= 2) {
      return (
        <Box sx={{ textAlign: 'center', padding: '24px' }}>
          <Alert severity="info" sx={{ mb: 2, maxWidth: 600, mx: 'auto' }}>
            No products found matching "{stableSearchTerm}". Try a different search term.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Try broader search terms or clear your filters
          </Typography>
        </Box>
      );
    }
    
    return (
      <div style={{ textAlign: 'center', padding: '24px' }}>
        No products found
      </div>
    );
  }

  return (
    <Box ref={!isGrid ? resultsRef : undefined}>
      {!isGrid && (
        <BulkActionToolbar
          selectedCount={selectedRows.length}
          onAction={handleBulkTrack}
          onClear={handleClearSelection}
          isLoading={isBulkTracking}
          actionType="track"
        />
      )}

      {isGrid ? (
        renderGrid()
      ) : (
        <div className={styles.container}>
          <DataGrid
            rows={isClientSide ? filteredStocks : stocks}
            columns={columns}
            getRowId={(row) => row.sku}
            paginationMode={isClientSide ? "client" : "server"}
            sortingMode={isClientSide ? "client" : "server"}
            paginationModel={paginationModel}
            onPaginationModelChange={(nextModel) => {
              updatePaginationModel(nextModel);
            }}
            sortModel={sortModel}
            onSortModelChange={(nextModel) => {
              updateSortModel(nextModel);
              if (paginationModel.page !== 0) {
                updatePaginationModel({ ...paginationModel, page: 0 });
              }
            }}
            rowCount={effectiveRowCount}
            loading={loading}
            pageSizeOptions={[10, 20, 50, 100]}
            getRowClassName={getRowClassName}
            onRowClick={handleRowClick}
            checkboxSelection={isPremium}
            disableRowSelectionOnClick
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRows(newSelection);
            }}
            isRowSelectable={() => isPremium}
            sx={{
              '& .MuiDataGrid-row': {
                cursor: 'pointer'
              },
              '& .MuiDataGrid-columnHeader': {
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              },
              '& .MuiDataGrid-cell': {
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }
            }}
          />
        </div>
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};
